import React, { useState } from 'react';
import { Clock, LogOut, Phone, UserCheck, User, Trash2 } from 'lucide-react';

import ImagePreviewModal from '../../components/ImagePreviewModal';
import api, { getApiErrorMessage, getAssetUrl } from '../../services/api';
import { formatDateTimeBR } from '../../utils/formatters';
import { EmptyState, LoadingState } from '../../components/ui/PageLayout';

const ContratadosTable = ({ contratados, loading, onDataChanged, onEdit }) => {
  const [pendingActionId, setPendingActionId] = useState(null);
  const [imagemPreview, setImagemPreview] = useState(null);

  const handleRegisterExit = async (id) => {
    if (window.confirm('Confirmar a saída deste prestador?')) {
      setPendingActionId(id);
      try {
        const { data } = await api.patch(`/contratados/${id}/saida`);
        await onDataChanged?.({ type: 'success', message: data.message || 'Saída registrada com sucesso.' });
      } catch (error) {
        await onDataChanged?.({ type: 'error', message: getApiErrorMessage(error, 'Erro ao registrar saída.') });
      } finally {
        setPendingActionId(null);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja excluir este prestador?')) {
      setPendingActionId(id);
      try {
        await api.delete(`/contratados/${id}`);
        await onDataChanged?.({ type: 'success', message: 'Prestador excluído com sucesso.' });
      } catch (error) {
        await onDataChanged?.({ type: 'error', message: getApiErrorMessage(error, 'Erro ao excluir prestador.') });
      } finally {
        setPendingActionId(null);
      }
    }
  };

  if (loading) {
    return <LoadingState message="Carregando prestadores..." />;
  }

  if (contratados.length === 0) {
    return <EmptyState message="Nenhum registro de prestador encontrado." />;
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="sticky top-0 z-10 bg-gray-50 text-xs uppercase tracking-wider text-gray-400 shadow-sm">
            <tr>
              <th className="min-w-[240px] px-6 py-4 font-semibold">Prestador</th>
              <th className="min-w-[180px] px-6 py-4 font-semibold">Contato</th>
              <th className="min-w-[200px] px-6 py-4 font-semibold">Responsável</th>
              <th className="min-w-[150px] px-6 py-4 text-center font-semibold">Entrada</th>
              <th className="min-w-[150px] px-6 py-4 text-center font-semibold">Saída</th>
              <th className="min-w-[190px] px-6 py-4 text-right font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {contratados.map((contratado) => (
              <tr key={contratado.id} className="hover:bg-gray-50/80 transition-colors group">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 overflow-hidden rounded-full bg-slate-100 flex items-center justify-center">
                      {contratado.foto_url ? (
                        <button
                          type="button"
                          onClick={() => setImagemPreview({ src: getAssetUrl(contratado.foto_url), alt: contratado.nome_completo })}
                          className="h-full w-full cursor-zoom-in"
                          title="Visualizar foto"
                        >
                          <img
                            src={getAssetUrl(contratado.foto_url)}
                            alt={contratado.nome_completo}
                            className="h-full w-full object-cover"
                          />
                        </button>
                      ) : (
                        <User size={18} className="text-slate-400" />
                      )}
                    </div>
                    <button type="button" onClick={() => onEdit(contratado)} disabled={pendingActionId === contratado.id} className="text-left font-medium text-slate-700 transition-colors hover:text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-60" title="Editar prestador">{contratado.nome_completo}</button>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-500 text-sm">
                  <div className="flex items-center gap-2">
                    {contratado.contato ? (
                      <a
                        href={`https://wa.me/55${contratado.contato.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-green-600 hover:text-green-700 font-medium transition-colors"
                        title="Chamar no WhatsApp"
                      >
                        <Phone size={14} />
                        {contratado.contato}
                      </a>
                    ) : (
                      <span className="text-gray-300 italic">Não informado</span>
                    )}
                  </div>
                </td>

                <td className="px-6 py-4 text-gray-500 text-sm">
                  <span className="font-semibold text-slate-600">{contratado.nome_morador || '---'}</span>
                </td>

                <td className="px-6 py-4 text-center">
                  <div className="flex flex-col items-center text-xs text-gray-500">
                    <Clock size={12} className="mb-1 text-blue-400" />
                    {formatDateTimeBR(contratado.data_entrada, 'dd/MM/yyyy HH:mm')}
                  </div>
                </td>

                <td className="px-6 py-4 text-center">
                  {contratado.data_saida ? (
                    <div className="flex flex-col items-center text-xs text-gray-400 italic">
                      {formatDateTimeBR(contratado.data_saida, 'dd/MM/yyyy HH:mm')}
                    </div>
                  ) : (
                    <span className="rounded-full bg-green-50 px-2 py-1 text-xs font-bold text-green-600">
                      No Local
                    </span>
                  )}
                </td>

                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <button
                      onClick={() => handleDelete(contratado.id)}
                      disabled={pendingActionId === contratado.id}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      title="Excluir prestador"
                    >
                      <Trash2 size={18} />
                    </button>
                    {!contratado.data_saida && (
                      <button
                        onClick={() => handleRegisterExit(contratado.id)}
                        disabled={pendingActionId === contratado.id}
                        title="Registrar Saída"
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 transition-all hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <LogOut size={16} />
                        {pendingActionId === contratado.id ? 'Salvando...' : 'Registrar Saída'}
                      </button>
                    )}
                    {contratado.data_saida && (
                      <UserCheck size={18} className="my-auto text-gray-300" title="Visita Finalizada" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ImagePreviewModal image={imagemPreview} onClose={() => setImagemPreview(null)} />
    </div>
  );
};

export default ContratadosTable;
